// @ts-nocheck

import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Skeleton } from '@mui/material';
import PropTypes from 'prop-types';

import SolyticsDrawer from '../../SolyticsDrawer';
import SolyticsButton from '../../SolyticsButton';
import SolyticsTableShowHideColumns from './SolyticsTableShowHideColumns';
import SolyticsTableColumnOrdering from './SolyticsTableColumnOrdering';
import SolyticsBox from '../../SolyticsBox';
import SolyticsTabs from '../../SolyticsTabs';

/**
 * ShowHideColumnsSkeleton - Skeleton placeholder for the Show/Hide tab
 */
const ShowHideColumnsSkeleton = () => (
  <SolyticsBox
    sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}
  >
    {/* Search skeleton */}
    <SolyticsBox sx={{ px: 2, pt: 2, pb: 1 }}>
      <Skeleton variant="rounded" height={40} sx={{ borderRadius: 1 }} />
    </SolyticsBox>
    {/* Select All skeleton */}
    <SolyticsBox
      sx={{ display: 'flex', alignItems: 'center', px: 2, height: 38 }}
    >
      <Skeleton variant="rounded" width={20} height={20} sx={{ mr: '18px' }} />
      <Skeleton variant="text" width={80} height={20} />
    </SolyticsBox>
    {/* Column rows skeleton */}
    {Array.from({ length: 8 }, (_, i) => (
      <SolyticsBox
        key={i}
        sx={{ display: 'flex', alignItems: 'center', px: 2, height: 38 }}
      >
        <Skeleton
          variant="rounded"
          width={20}
          height={20}
          sx={{ mr: '18px' }}
        />
        <Skeleton variant="text" width={`${60 + (i % 3) * 15}%`} height={20} />
      </SolyticsBox>
    ))}
  </SolyticsBox>
);

/**
 * ColumnOrderingSkeleton - Skeleton placeholder for the Reorder tab
 */
const ColumnOrderingSkeleton = () => (
  <SolyticsBox
    sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}
  >
    {/* Quick Move skeleton */}
    <SolyticsBox
      sx={{
        backgroundColor: (theme) => theme.palette.grey?.[30] || '#f4f4f5',
        borderBottom: (theme) =>
          `1px solid ${theme.palette.grey?.[50] || theme.palette.divider}`,
        px: 2,
        pt: '12px',
        pb: '16px',
      }}
    >
      <Skeleton variant="text" width={250} height={22} sx={{ mb: '10px' }} />
      <SolyticsBox sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
        <Skeleton
          variant="rounded"
          width={207}
          height={40}
          sx={{ borderRadius: 1 }}
        />
        <Skeleton variant="circular" width={18} height={18} />
        <Skeleton
          variant="rounded"
          width={100}
          height={40}
          sx={{ borderRadius: 1 }}
        />
        <Skeleton
          variant="rounded"
          width={60}
          height={40}
          sx={{ borderRadius: 1 }}
        />
      </SolyticsBox>
    </SolyticsBox>
    {/* Column rows skeleton */}
    <SolyticsBox sx={{ px: 2, pt: 1 }}>
      {Array.from({ length: 8 }, (_, i) => (
        <SolyticsBox
          key={i}
          sx={{ display: 'flex', alignItems: 'center', gap: '8px', height: 38 }}
        >
          <Skeleton variant="rounded" width={18} height={18} />
          <Skeleton
            variant="rounded"
            width={24}
            height={24}
            sx={{ borderRadius: '4px' }}
          />
          <Skeleton
            variant="text"
            width={`${50 + (i % 4) * 12}%`}
            height={20}
            sx={{ flex: 1 }}
          />
        </SolyticsBox>
      ))}
    </SolyticsBox>
  </SolyticsBox>
);

/**
 * SolyticsTableSettings - Drawer with tabbed interface for managing table column visibility and order
 *
 * Features:
 * - Tab 1: Show/Hide Columns - toggle column visibility with search and select all
 * - Tab 2: Reorder Columns - drag-and-drop + quick move to reorder columns
 * - Save/Cancel - pending changes are only applied on Save
 * - Virtualization support for large column counts
 *
 * @param {Object} props
 * @param {boolean} props.open - Whether the drawer is open
 * @param {Function} props.onClose - Callback when drawer closes (without saving): () => void
 * @param {Array} props.columns - Array of column objects with { id, header, accessorKey } or TanStack column definitions
 * @param {Object} props.columnVisibility - Current column visibility map: { [columnId]: boolean }
 * @param {Function} props.onColumnVisibilityChange - Callback when column visibility is saved: (visibility) => void
 * @param {Array} props.columnOrder - Current column order array of column IDs
 * @param {Function} props.onColumnOrderChange - Callback when column order is saved: (columnOrder) => void
 * @param {number} props.enableVirtualization - Threshold for enabling virtualized list
 * @param {boolean} props.loading - Show skeleton loading state
 * @param {string} props.title - Drawer title (default: "Table Settings")
 */
const SolyticsTableSettings = ({
  open,
  onClose,
  columns = [],
  columnVisibility = {},
  onColumnVisibilityChange,
  columnOrder = [],
  onColumnOrderChange,
  onSettingsSave,
  listVirtuliazationThreshold,
  loading = false,
  title = 'Table Settings',
}) => {
  const [tabValue, setTabValue] = useState(0);
  const [pendingColumnVisibility, setPendingColumnVisibility] = useState({});
  const [pendingColumnOrder, setPendingColumnOrder] = useState(null);

  // Extract column info from TanStack column definitions or plain objects
  const columnInfo = useMemo(() => {
    return columns.map((col) => ({
      id: col.id || col.accessorKey || col.header,
      header:
        typeof col.header === 'string'
          ? col.header
          : col.id || col.accessorKey || '',
      accessorKey: col.accessorKey || col.id,
      disableHiding: col.disableHiding,
      disabledOrderingCols: col.disabledOrderingCols,
      disableColumnFreezing: col.disableColumnFreezing,
    }));
  }, [columns]);

  // Initialize pending state when drawer opens
  useEffect(() => {
    if (open) {
      // Build initial visibility from current state
      const initialVisibility = {};
      columnInfo.forEach((col) => {
        initialVisibility[col.id] =
          columnVisibility[col.id] !== undefined
            ? columnVisibility[col.id]
            : true;
      });
      setPendingColumnVisibility(initialVisibility);
      setPendingColumnOrder(
        columnOrder.length > 0 ? [...columnOrder] : columnInfo.map((c) => c.id)
      );
      setTabValue(0);
    }
  }, [open, columnInfo, columnVisibility, columnOrder]);

  const handleTabChange = useCallback(
    (_event, newValue) => {
      setTabValue(newValue);
    },
    [setTabValue]
  );

  const handleSaveChanges = useCallback(() => {
    const visibilityMap = {};
    columnInfo.forEach((col) => {
      visibilityMap[col.id] =
        pendingColumnVisibility[col.id] !== undefined
          ? pendingColumnVisibility[col.id]
          : true;
    });

    if (onSettingsSave) {
      onSettingsSave(visibilityMap, pendingColumnOrder || undefined);
    } else {
      if (onColumnVisibilityChange) {
        onColumnVisibilityChange(visibilityMap);
      }
      if (onColumnOrderChange && pendingColumnOrder) {
        onColumnOrderChange(pendingColumnOrder);
      }
    }

    onClose?.();
  }, [
    onClose,
    onSettingsSave,
    onColumnVisibilityChange,
    onColumnOrderChange,
    columnInfo,
    pendingColumnVisibility,
    pendingColumnOrder,
  ]);

  const handleCancelChanges = useCallback(() => {
    onClose?.();
  }, [onClose]);

  return (
    <SolyticsDrawer
      open={open}
      disableContentPadding={true}
      handleClose={handleCancelChanges}
      header={title}
      aria-labelledby="table-settings-title"
      content={
        <SolyticsTabs
          value={tabValue}
          onChange={(val) => handleTabChange(null, val)}
          variant="underlined"
          tabBarPaddingX={2}
          items={[
            {
              label: 'Show/Hide',
              value: 0,
              content: loading ? (
                <ShowHideColumnsSkeleton />
              ) : (
                <SolyticsTableShowHideColumns
                  columns={columnInfo}
                  listVirtuliazationThreshold={listVirtuliazationThreshold}
                  pendingColumnOrder={pendingColumnOrder}
                  pendingColumnVisibility={pendingColumnVisibility}
                  setPendingColumnVisibility={setPendingColumnVisibility}
                />
              ),
            },
            {
              label: 'Reorder Column',
              value: 1,
              content: loading ? (
                <ColumnOrderingSkeleton />
              ) : (
                <SolyticsTableColumnOrdering
                  columns={columnInfo}
                  pendingColumnOrder={pendingColumnOrder}
                  setPendingColumnOrder={setPendingColumnOrder}
                  pendingColumnVisibility={pendingColumnVisibility}
                  listVirtuliazationThreshold={listVirtuliazationThreshold}
                />
              ),
            },
          ]}
        />
      }
      footer={
        <>
          <SolyticsButton
            onClick={handleCancelChanges}
            variant="contained"
            color="secondary"
          >
            Cancel
          </SolyticsButton>
          <SolyticsButton onClick={handleSaveChanges} variant="contained">
            Save
          </SolyticsButton>
        </>
      }
    />
  );
};

SolyticsTableSettings.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      header: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
      accessorKey: PropTypes.string,
      disableHiding: PropTypes.bool,
      disabledOrderingCols: PropTypes.bool,
      disableColumnFreezing: PropTypes.bool,
    })
  ),
  columnVisibility: PropTypes.objectOf(PropTypes.bool),
  onColumnVisibilityChange: PropTypes.func,
  columnOrder: PropTypes.arrayOf(PropTypes.string),
  onColumnOrderChange: PropTypes.func,
  onSettingsSave: PropTypes.func,
  listVirtuliazationThreshold: PropTypes.number,
  loading: PropTypes.bool,
  title: PropTypes.string,
};

export default SolyticsTableSettings;
